import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Divider, Paper, TextField } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { SimpleTreeView, TreeItem } from '@mui/x-tree-view';
import { useDebounce } from 'use-debounce';
import { OntologyTreeNode } from './OntologyTreeNode';
import { useSearchOntology } from '../../hooks/api/use-ontology.hook';
import { NodeType } from '../../../../types/constants/node-type';
import { LoadingIndicator } from '../LoadingIndicator';
import { NodeApiModel } from '../../../../types/models/node.model';

export const Ontology = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [nodes, setNodes] = useState<NodeApiModel[]>([]);
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebounce(search, 250);

  const { loading, response, fetchNextPage } = useSearchOntology({
    name: debouncedSearch || undefined,
    description: debouncedSearch || undefined,
    parentId: null,
    nodeType: debouncedSearch ? NodeType.MASTER : NodeType.CATEGORY,
  });

  useEffect(() => {
    if (response) {
      setNodes(response.pages.map((page) => page.result).flat());
    }
  }, [response]);

  const handleScroll = useCallback(() => {
    if (
      containerRef.current &&
      containerRef.current.scrollTop + containerRef.current.clientHeight >= containerRef.current.scrollHeight - 60
    ) {
      fetchNextPage();
    }
  }, []);

  return (
    <Box display="flex" flexDirection="column">
      <TextField
        size="small"
        label="Search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        slotProps={{ input: { startAdornment: <SearchIcon /> } }}
        sx={{ m: '15px' }}
      />
      <Divider />
      <Box flexGrow={1}>
        <Paper
          sx={{ marginTop: '20px', padding: '4px', height: '100vh', overflow: 'auto' }}
          ref={containerRef}
          onScroll={handleScroll}
        >
          <SimpleTreeView>
            {nodes.map((node) => (
              <OntologyTreeNode key={node.id} node={node} />
            ))}
            {loading ? <TreeItem itemId={`root-loading`} label={<LoadingIndicator />} /> : null}
          </SimpleTreeView>
        </Paper>
      </Box>
    </Box>
  );
};
